package com.kinoshazam.uiKit.components.videoItem

import androidx.compose.foundation.BorderStroke
import androidx.compose.foundation.ExperimentalFoundationApi
import androidx.compose.foundation.basicMarquee
import androidx.compose.foundation.border
import androidx.compose.foundation.clickable
import androidx.compose.foundation.interaction.MutableInteractionSource
import androidx.compose.foundation.interaction.collectIsHoveredAsState
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.aspectRatio
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.heightIn
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.layout.wrapContentHeight
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.remember
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.layout.ContentScale
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.Dp
import androidx.compose.ui.unit.dp
import com.kinoshazam.uiKit.components.image.ImageUiKitState
import com.kinoshazam.uiKit.components.image.UiKitImage
import com.kinoshazam.uiKit.theme.Large
import com.kinoshazam.uiKit.theme.Medium
import com.kinoshazam.uiKit.theme.UiKitTheme
import com.kinoshazam.uiKit.theme.largeRadius
import com.kinoshazam.uiKit.theme.middleRadius

/** Poster width : height used by medium cards (matches previous 160×230 art). */
const val MediumVideoPosterAspectRatio = 160f / 230f

val MediumVideoCardWidth = 160.dp
val MediumVideoCardWidthCompact = 120.dp

@OptIn(ExperimentalFoundationApi::class)
@Composable
fun MediumVideoItem(
    state: VideoState,
    isFocused: Boolean = false,
    modifier: Modifier = Modifier,
    width: Dp = MediumVideoCardWidth,
    showFocusBorder: Boolean = true,
) {
    val interactionSource = remember { MutableInteractionSource() }
    val isHovered by interactionSource.collectIsHoveredAsState()
    val highlighted = isHovered || isFocused
    val titleStyle = if (width < MediumVideoCardWidth) Medium else Large
    val corner = if (width < MediumVideoCardWidth) middleRadius else largeRadius
    val borderWidth = if (width < MediumVideoCardWidth) 3.dp else 4.dp

    Column(
        // width is applied after external modifiers so padding can't drop the fixed size
        modifier = modifier
            .width(width)
            .wrapContentHeight()
            .clip(shape = corner)
            .clickable(
                interactionSource = interactionSource,
                indication = null,
                onClick = { state.onAction(state.id) },
            )
    ) {
        val posterModifier = Modifier
            .fillMaxWidth()
            .aspectRatio(MediumVideoPosterAspectRatio)
        Box(
            modifier = if (highlighted && showFocusBorder) {
                posterModifier.border(
                    border = BorderStroke(borderWidth, UiKitTheme.colors.color_1),
                    shape = corner,
                )
            } else {
                posterModifier
            }
        ) {
            UiKitImage(
                state = ImageUiKitState(
                    url = state.image,
                    placeholder = state.placeHolder,
                    placeholderColor = state.placeHolderColor,
                    contentScale = ContentScale.Crop,
                ),
                modifier = Modifier
                    .fillMaxSize()
                    .align(Alignment.Center)
                    .padding(start = 1.dp, end = 1.dp, top = 1.dp)
                    .clip(shape = corner)
            )
        }

        val textModifier = Modifier
            .fillMaxWidth()
            .heightIn(min = 32.dp)
            .padding(horizontal = 4.dp, vertical = 4.dp)
            .then(
                if (highlighted) Modifier.basicMarquee() else Modifier
            )

        Text(
            text = state.title,
            color = if (highlighted) UiKitTheme.colors.color_1 else UiKitTheme.colors.color_2,
            maxLines = 1,
            overflow = if (highlighted) TextOverflow.Clip else TextOverflow.Ellipsis,
            style = titleStyle,
            softWrap = false,
            modifier = textModifier,
        )
    }
}
